import { BrandedEmailsSettingsRepository, EmailType } from '@packlink/packlink-sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useToast } from '@shipengine/giger';
import { useTranslation } from '@packlink/translation-provider';
import { BRANDED_EMAILS_SETTINGS_CACHE_KEY } from './useBrandedEmailsSettings';

interface ToggleEmailTemplateParams {
    emailType: EmailType;
    active: boolean;
    onSuccess?: () => void;
    onError?: () => void;
    onToggle?: () => void;
}

export const useToggleActiveTemplate = () => {
    const { t } = useTranslation();
    const toast = useToast(t);
    const { apiClient } = useSdk();
    const queryClient = useQueryClient();
    const brandedEmailsSettingsRepository = new BrandedEmailsSettingsRepository(apiClient);

    const mutation = useMutation({
        mutationFn: (params: { emailType: EmailType; active: boolean }) => {
            return brandedEmailsSettingsRepository.toggle(params.emailType, params.active);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [BRANDED_EMAILS_SETTINGS_CACHE_KEY] });
        },
        onError: () => {
            toast.error({
                message: t('branded-emails.error.try-again'),
            });
        },
    });

    const toggleActiveTemplate = ({ emailType, active, onSuccess, onError, onToggle }: ToggleEmailTemplateParams) => {
        mutation.mutate(
            { emailType, active },
            {
                onSuccess: () => {
                    onSuccess?.();
                    onToggle?.();
                },
                onError: () => {
                    onToggle?.();
                    onError?.();
                },
            },
        );
    };

    return {
        toggleActiveTemplate,
        isLoading: mutation.isPending,
    };
};
